/**
 * Canonical EMPTY / LOADING / ERROR state vocabulary (design-system §7).
 *
 * One trio, reused everywhere — lists, detail pages, panels — so "nothing here",
 * "still loading", and "couldn't load" always look and read the same:
 *
 *   • LoadingState  — Skeleton bars, never a spinner for content (§7). The
 *                     restrained, layout-preserving wait state.
 *   • EmptyState    — the shadcn `Empty` primitive: a muted `<Icon>`, a one-line
 *                     title, a one-line hint, and an optional action. The ONLY
 *                     sanctioned `border-dashed` lives here (in `Empty`).
 *   • ErrorState    — a calm inline destructive block with an optional Retry.
 *                     Toasts are for actions; load failures degrade in place.
 *
 * These wrap the shadcn `ui/empty` + `ui/skeleton` primitives so the rest of the
 * app imports STATES, not ad-hoc `<p>Loading…</p>` / `<div>No data</div>` markup.
 * The table helpers (`TableSkeletonRows`, `TableStateRow`) render the same trio
 * inside a `<TableBody>` for the shared DataTable family.
 */
import * as React from "react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { TableCell, TableRow } from "@/components/ui/table";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { Icon, type IconName } from "@/components/ui/icon";

/* ------------------------------------------------------------------ Loading */

export interface LoadingStateProps {
  /**
   * Accessible label announced while loading (kept for back-compat with the
   * legacy spinner API). Rendered visually-hidden — the visible state is the
   * Skeleton, never text.
   */
  label?: string;
  /** Number of skeleton rows to render (default 5). */
  rows?: number;
  className?: string;
}

/**
 * Standalone loading state: layout-preserving Skeleton bars (§7 — never a
 * spinner for content). Use inside lists/panels/detail pages while a query is
 * in flight. For in-table loading use `TableSkeletonRows`.
 */
export function LoadingState({
  label = "Loading…",
  rows = 5,
  className,
}: LoadingStateProps) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-live="polite"
      className={cn("space-y-3 py-4", className)}
    >
      <span className="sr-only">{label}</span>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-4">
          <Skeleton className="h-4 w-1/4" />
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="h-4 w-16" />
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------- Empty */

export interface EmptyStateProps {
  /** Optional muted glyph at the top of the block. */
  icon?: IconName;
  /**
   * One-line title. Back-compat: callers that pass only `message` get it as the
   * title when no `title` is supplied.
   */
  title?: React.ReactNode;
  /** Legacy single-line message (maps to `title` when `title` is absent). */
  message?: string;
  /** One-line muted hint under the title. */
  hint?: React.ReactNode;
  /** Optional primary action (e.g. a create Button). */
  action?: React.ReactNode;
  className?: string;
}

/**
 * Empty state built on the shadcn `Empty` primitive (the one place
 * `border-dashed` is allowed): a muted icon, a one-line title, a one-line hint,
 * and an optional action. Back-compat: `<EmptyState message="…" />` still works.
 */
export function EmptyState({
  icon,
  title,
  message,
  hint,
  action,
  className,
}: EmptyStateProps) {
  const heading = title ?? message ?? "Nothing here yet.";
  return (
    <Empty className={className}>
      <EmptyHeader>
        {icon ? (
          <EmptyMedia>
            <Icon name={icon} aria-hidden />
          </EmptyMedia>
        ) : null}
        <EmptyTitle>{heading}</EmptyTitle>
        {hint ? <EmptyDescription>{hint}</EmptyDescription> : null}
      </EmptyHeader>
      {action ? <EmptyContent>{action}</EmptyContent> : null}
    </Empty>
  );
}

/* -------------------------------------------------------------------- Error */

export interface ErrorStateProps {
  /** The thrown error (its `message` is surfaced when present). */
  error?: unknown;
  /** Override the body message (takes precedence over `error.message`). */
  message?: string;
  /** Optional Retry handler; renders a calm outline button when provided. */
  onRetry?: () => void;
  className?: string;
}

/**
 * Calm inline error block (§7 — load failures degrade in place, not as toasts):
 * a destructive-tinted panel with a heading, the backend message, and an
 * optional Retry.
 */
export function ErrorState({
  error,
  message,
  onRetry,
  className,
}: ErrorStateProps) {
  const body =
    message ??
    (error as { message?: string } | null | undefined)?.message ??
    "Something went wrong while loading data.";
  return (
    <div
      role="alert"
      className={cn(
        "rounded-md border border-destructive/40 bg-destructive/5 px-4 py-6 text-sm",
        className,
      )}
    >
      <p className="font-medium text-destructive">Failed to load</p>
      <p className="mt-1 text-destructive/80">{body}</p>
      {onRetry ? (
        <Button
          variant="outline"
          size="sm"
          className="mt-3"
          onClick={onRetry}
        >
          Retry
        </Button>
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------- Table-body forms */

/**
 * Skeleton rows for a `<TableBody>` loading state (§7 — never a spinner inside
 * a table). Mirrors the column count so the table keeps its shape while loading.
 */
export function TableSkeletonRows({
  columns,
  rows = 6,
}: {
  columns: number;
  rows?: number;
}) {
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <TableRow key={r} className="hover:bg-transparent">
          {Array.from({ length: columns }).map((_, c) => (
            <TableCell key={c}>
              <Skeleton className="h-4 w-full max-w-[10rem]" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

/**
 * A single full-width `<TableRow>` that hosts a centered Empty or Error state in
 * the body of an otherwise-structured table.
 */
export function TableStateRow({
  columns,
  children,
}: {
  columns: number;
  children: React.ReactNode;
}) {
  return (
    <TableRow className="hover:bg-transparent">
      <TableCell colSpan={columns} className="p-4">
        {children}
      </TableCell>
    </TableRow>
  );
}
